import React, { ReactNode } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import useArb from "../../hooks/use-arb";
import {
  getBgcStatus,
  getDtStatus,
  getI9Status,
  ScreenResult,
  TRANSLATED_SCREEN_RESULT,
} from "../../utility/screening-results-helper";
import { H1 } from "../typography";
import {
  IconCheckCircleFill,
  IconCloseCircleFill,
} from "@amzn/stencil-react-components/icons";

import ProgressRing from "./progress-ring.svg";
import { Text } from "@amzn/stencil-react-components/text";
import {
  defaultTextColor,
  statusCompleteCheckColor,
  statusErrorColor,
  statusPendingColor,
} from "../../config/palette";
import { Link } from "@amzn/stencil-react-components/link";
import { buildUrl, CONTACT_US } from "../../config/urls";
import { Stage } from "../../helpers/get-settings";
import { I9Response } from "../i9-task/i9-controlled";
import { ContingencyResponse } from "./card-uncontrolled";

export interface ScreeningResultsControlledProperty {
  dtResponse?: ContingencyResponse;
  bgcResponse?: ContingencyResponse;
  i9Response?: I9Response;
  loading: boolean;
  error: boolean;
  stage?: Stage;
}

export interface ScreeningResultsControlledInput {
  property: ScreeningResultsControlledProperty;
}

interface Result {
  label: string;
  status: string;
}

export const ScreeningResultsControlled = ({
  property,
}: ScreeningResultsControlledInput) => {
  const bundle = useArb("HVHChecklistPantherResource");
  const loadingText = bundle.getMessage("checklist-General-TaskStatus-Loading");

  if (property.loading) {
    return (
      <Col alignItems="left">
        <Spinner
          size={SpinnerSize.Small}
          loadingText={loadingText}
          showText={true}
        />
      </Col>
    );
  }

  if (property.error || !property.stage) {
    return null;
  }

  const i9Status = !property.i9Response
    ? ScreenResult.Error
    : getI9Status(property.i9Response);
  const bgcStatus = !property.bgcResponse?.status
    ? ScreenResult.Error
    : getBgcStatus(property.bgcResponse.status);
  const dtStatus = !property.dtResponse?.status
    ? ScreenResult.Error
    : getDtStatus(property.dtResponse.status);

  if (
    i9Status !== ScreenResult.Complete &&
    bgcStatus !== ScreenResult.Complete &&
    dtStatus !== ScreenResult.Complete
  ) {
    return null;
  }

  let containsError;
  if (
    i9Status === ScreenResult.Error ||
    bgcStatus === ScreenResult.Error ||
    dtStatus === ScreenResult.Error
  ) {
    containsError = true;
  }

  const results = [
    {
      label: bundle.getMessage("Checklist-MainPage-I9-Title"),
      status: i9Status,
    },
    {
      label: bundle.getMessage("Checklist-MainPage-BGC-Title"),
      status: bgcStatus,
    },
    {
      label: bundle.getMessage("Checklist-MainPage-DT-Title"),
      status: dtStatus,
    },
  ] as Array<Result>;

  const getStatusIcon = (status: string): ReactNode => {
    if (status === ScreenResult.Complete) {
      return <IconCheckCircleFill color={statusCompleteCheckColor} />;
    } else if (status === ScreenResult.Pending) {
      return <img src={ProgressRing} alt="progress-ring" />;
    } else {
      return <IconCloseCircleFill color={statusErrorColor} />;
    }
  };

  const title = bundle.getMessage("Checklist-MainPage-Screening-Results");
  const stage = property.stage;

  return (
    <>
      <Col gridGap="1rem">
        <Row alignItems={"center"} justifyContent={"space-between"}>
          <H1>{title}</H1>
          {containsError && (
            <Link
              target="_blank"
              href={buildUrl(CONTACT_US, {
                stage,
              })}
            >
              {bundle.getMessage("Checklist-MainPage-Contact-US")}
            </Link>
          )}
        </Row>

        {results.map((result: Result, index: number) => {
          return (
            <Row key={index} gridGap={"0.3rem"} alignItems={"center"}>
              {getStatusIcon(result.status)}
              <Text
                fontSize={"T200"}
                color={
                  result.status === ScreenResult.Pending
                    ? statusPendingColor
                    : defaultTextColor
                }
              >
                {result.label}{" "}
                <strong>
                  &middot;{" "}
                  {bundle.getMessage(TRANSLATED_SCREEN_RESULT[result.status])}
                </strong>
              </Text>
            </Row>
          );
        })}
      </Col>
    </>
  );
};
